import { Injectable } from '@angular/core';

import { DateTime } from 'luxon';

import { Breadcrumb } from '@ptg-shared/types/models/breadcrumb.model';
import { getDateString, isEmpty } from '@ptg-shared/utils/string.util';
import { deepClone, toTimeZoneLocal } from '@ptg-shared/utils/common.util';

import {
  DependentType,
  CalculationType,
  CalculationBenefitHistoryStatus,
  ValidateDisabilityBenefitErrorType,
} from '../../types/enums';
import {
  DependentInformationItem,
  CalculationDetailDisplayData,
  GetDisabilityCalculationDetailResponse,
  DisabilityBenefit,
  StepperState,
  AuditTrailLine,
} from '../../services/models';

@Injectable()
export class DisabilityCalculationDetailComponentService {
  getBreadcrumbs(
    calculationType: CalculationType,
    memberId: string = '',
    calculationBenefitId: string = '',
  ): Breadcrumb[] {
    return [
      {
        name: 'Overview',
        url: `/member/disability-overview/${calculationType}/${memberId}/detail/${calculationBenefitId}`,
      },
      {
        name: `Disability Calculation Detail`,
      },
    ];
  }

  getErrorMessageByType(errorType?: ValidateDisabilityBenefitErrorType, actionType: string = 'complete'): string {
    switch (errorType) {
      case ValidateDisabilityBenefitErrorType.NotInitiatedStatus:
        return `Can only ${actionType} calculation record at status "Initiated".`;
      case ValidateDisabilityBenefitErrorType.NotPendingApproved:
        return 'Can only approve calculation record at status "Pending approval".';
      case ValidateDisabilityBenefitErrorType.Exception:
        return 'Please resolve exception to proceed.';
      case ValidateDisabilityBenefitErrorType.InformationHasBeenChanged:
        return 'Some information has been changed and might affect calculation. Please re-calculate before proceeding.';
      case ValidateDisabilityBenefitErrorType.NeedToFillInAllInfo:
        return 'Please fill in all information to proceed.';
      default:
        return 'Error occurred. Please try again.';
    }
  }

  getStepperState(auditTrailLines: AuditTrailLine[] = []): StepperState | null {
    if (!auditTrailLines.length) {
      return null;
    }

    const optionalLabels: string[] = deepClone(auditTrailLines)
      .sort((a: any, b: any) => a.step - b.step)
      .map((item: any) => {
        const completeTimeToLocal = item.submittedDate
          ? toTimeZoneLocal(new Date(item.submittedDate)).toISOString()
          : '';
        const completeTime = DateTime.fromISO(completeTimeToLocal).toFormat('MM/dd/yyyy hh:mm a');
        const label = item.submittedBy ? `${item.submittedBy} - ${completeTime}` : '';

        return label;
      });

    const currentStep = auditTrailLines.filter((itemAudit) => !!itemAudit).length;

    const stepperState = {
      selectedIndex: auditTrailLines.length - 1,
      currentStep,
      labels: ['Calculate', 'Approve'],
      optionalLabels,
    };
    const completedStep = auditTrailLines.length;

    return {
      stepperState,
      completedStep,
    };
  }

  handleDataDisplay(
    detailData: GetDisabilityCalculationDetailResponse,
    limitReasonLength = 390,
  ): CalculationDetailDisplayData | null {
    if (detailData.benefitCalculation === undefined) {
      return null;
    }

    const calculationType = detailData.calculationType;
    const isLongTerm = detailData.calculationType === CalculationType.DisabilityLongTerm;
    const isShowEditBtn = detailData.benefitCalculation?.status === CalculationBenefitHistoryStatus.Initiated;
    const recalReason = {
      isTruncate: true,
      note: detailData.benefitCalculation?.recalReason ?? '',
    };

    const itemDetail = detailData.benefitCalculation;
    const childNumber: number = detailData.dependentInformationList.filter(
      (dependItem: DependentInformationItem) => dependItem.type === DependentType.Child,
    )?.length;

    const benefitCalculationData = {
      ...detailData.benefitCalculation,
      memberAmount: this.handleAmountValue(+detailData.benefitCalculation?.memberAmount),
      spouseAmount: this.handleAmountValue(+detailData.benefitCalculation?.spouseAmount),
      childAmount: this.handleAmountValue(+detailData.benefitCalculation?.childAmount),
      childNumber,
      totals: this.calculateTotalsBenefit(itemDetail, isLongTerm, childNumber),
    };

    const isShowSeeMore = detailData.benefitCalculation?.recalReason?.length > limitReasonLength;
    const canRecalculate = itemDetail.status === CalculationBenefitHistoryStatus.Approved;
    const isShowReason =
      detailData.numberOfCalculationRecordsInBenefit > 1 &&
      !isEmpty(itemDetail.recalReason) &&
      itemDetail.recalReason != '';
    const calcAsOfDate = itemDetail.calcAsOfDate;
    const calcEndDate = itemDetail.benefitEndDate;

    const dependentsInfor = deepClone(
      detailData.dependentInformationList?.map((item: any) => {
        return {
          ...item,
          relationship: DependentType[item.type],
          ssn: this.renderSSNDisplay(item.ssn),
          dateOfBirth: getDateString(item.dateOfBirth),
          dateOfDeath: getDateString(item.dateOfDeath),
        };
      }) ?? [],
    );

    return {
      calculationType,
      isLongTerm,
      isShowEditBtn,
      isShowSeeMore,
      isShowReason,
      canRecalculate,
      calcAsOfDate,
      calcEndDate,
      recalReason,
      dependentsInfor,
      benefitCalculationData,
    };
  }

  private renderSSNDisplay(input: string) {
    if (isEmpty(input) || input === '') {
      return '-';
    }
    return `XXX-XX-${input.slice(-4)}`;
  }

  private handleAmountValue(input: number): number | string {
    if (!input || input == -1) {
      return '';
    }
    return input;
  }

  private calculateTotalsBenefit(item: DisabilityBenefit, isLongTerm: boolean, childNumber = 0): number {
    if (!isLongTerm) {
      return 0;
    }
    const memberAmount = this.handleAmountValue(+item.memberAmount) ?? 0;
    const spouseAmount = this.handleAmountValue(+item.spouseAmount) ?? 0;
    const childAmount = this.handleAmountValue(+item.childAmount) ?? 0;
    return +memberAmount + +spouseAmount + +childAmount * childNumber;
  }
}
